import { createSlice } from '@reduxjs/toolkit';
import { fetchPutaway, putawayRow, putawayRows, uploadPutaway } from './putawayThunks';
import { ALL_CLIENTS, EMPTY_PUTAWAY_COUNTS, PUTAWAY_TAB } from './putawayUtils';
import { selectWarehouse } from '../warehouseContext/warehouseContextSlice';

const initialState = {
  /** The rows of the tab in use. Pending rows and history rows are different shapes. */
  rows: [],
  /** The tab `rows` belong to, so the table never draws them under another one. */
  rowsTab: PUTAWAY_TAB.RECEIVING,
  /** How many rows and units each pending source has, whichever tab is open. */
  counts: EMPTY_PUTAWAY_COUNTS,
  /** Total of the history, which is the only tab paginated on the server. */
  historyCount: 0,
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchPutaway.fulfilled`. */
  requestId: null,
  filters: {
    tab: PUTAWAY_TAB.RECEIVING,
    company: ALL_CLIENTS,
    page: 1,
    rowsPerPage: 20,
  },

  /** The `key` of the row being put away. Its failure is told by whoever asked. */
  busyKey: null,
  /** A sheet on its way. The dialog that sent it reads it. */
  uploading: false,
};

const putawaySlice = createSlice({
  name: 'putaway',
  initialState,
  reducers: {
    /** Merges a patch into the filters; anything but a page change goes back to page 1. */
    setPutawayFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPutaway.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchPutaway.fulfilled, (state, action) => {
        // Only the latest request may write: a tab can be changed while the
        // rows of the previous one are still on their way.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.rows = action.payload.rows;
        state.rowsTab = action.payload.tab;
        // The history does not bring the counters, and the ones already known
        // are still the latest word about the pending tabs.
        if (action.payload.counts) {
          state.counts = { ...EMPTY_PUTAWAY_COUNTS, ...action.payload.counts };
        }
        if (action.payload.tab === PUTAWAY_TAB.HISTORY) {
          state.historyCount = action.payload.count;
        }
      })
      .addCase(fetchPutaway.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
        state.rows = [];
      })

      .addCase(putawayRow.pending, (state, action) => {
        state.busyKey = action.meta.arg.row.key;
      })
      .addCase(putawayRow.fulfilled, (state) => {
        state.busyKey = null;
      })
      .addCase(putawayRow.rejected, (state) => {
        state.busyKey = null;
      })

      // A bulk save holds every row's buttons, like one row's save does.
      .addCase(putawayRows.pending, (state) => {
        state.busyKey = 'bulk';
      })
      .addCase(putawayRows.fulfilled, (state) => {
        state.busyKey = null;
      })
      .addCase(putawayRows.rejected, (state) => {
        state.busyKey = null;
      })

      .addCase(uploadPutaway.pending, (state) => {
        state.uploading = true;
      })
      .addCase(uploadPutaway.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadPutaway.rejected, (state) => {
        state.uploading = false;
      })

      // Another warehouse is other stock, other clients and other counters.
      .addCase(selectWarehouse, (state) => {
        state.filters.company = ALL_CLIENTS;
        state.filters.page = 1;
        state.rows = [];
        state.counts = EMPTY_PUTAWAY_COUNTS;
        state.historyCount = 0;
      });
  },
});

export const { setPutawayFilters } = putawaySlice.actions;

export default putawaySlice.reducer;
